import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { makeRequest } from "../../utils/request";

export type BillingUsageData = {
  totalDurationMs: number;
  totalDurationMinutes: number;
  totalSessions: number;
  agentBreakdown: {
    agentId: string;
    agentName: string;
    durationMs: number;
    durationMinutes: number;
    sessionCount: number;
  }[];
};

export type BillingPeriodData = {
  id?: string; // For Redux slice compatibility
  startDate: string;
  endDate: string;
  organizationId: string;
  usage: BillingUsageData;
};

export type BillingReportParams = {
  startDate: string;
  endDate: string;
  organizationId?: string;
};

export type MonthlyBillingParams = {
  year: string;
  month: string;
  organizationId?: string;
};

export type BillingState = {
  currentReport: BillingPeriodData | null;
  isLoading: boolean;
  error: string | null;
};

// Async thunks
export const getBillingReport = createAsyncThunk("billing/getBillingReport", async (params: BillingReportParams) => {
  const queryString = Object.entries(params)
    .filter(([_, value]) => value !== undefined)
    .map(([key, value]) => `${key}=${value}`)
    .join("&");

  const { data } = await makeRequest<{ data: BillingPeriodData }>(`/v1/billing/report?${queryString}`);
  return data;
});

export const getMonthlyBilling = createAsyncThunk("billing/getMonthlyBilling", async (params: MonthlyBillingParams) => {
  const queryString = Object.entries(params)
    .filter(([_, value]) => value !== undefined)
    .map(([key, value]) => `${key}=${value}`)
    .join("&");

  const { data } = await makeRequest<{ data: BillingPeriodData }>(`/v1/billing/monthly?${queryString}`);
  return data;
});

const initialState: BillingState = {
  currentReport: null,
  isLoading: false,
  error: null,
};

const billingSlice = createSlice({
  name: "billing",
  initialState,
  reducers: {
    clearBillingData: (state) => {
      state.currentReport = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // getBillingReport cases
    builder.addCase(getBillingReport.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(getBillingReport.fulfilled, (state, action) => {
      state.isLoading = false;
      state.currentReport = action.payload;
      state.error = null;
    });
    builder.addCase(getBillingReport.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message || "Failed to fetch billing report";
    });

    // getMonthlyBilling cases
    builder.addCase(getMonthlyBilling.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(getMonthlyBilling.fulfilled, (state, action) => {
      state.isLoading = false;
      state.currentReport = action.payload;
      state.error = null;
    });
    builder.addCase(getMonthlyBilling.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message || "Failed to fetch monthly billing";
    });
  },
});

export const { clearBillingData } = billingSlice.actions;
export const billingReducer = billingSlice.reducer;
